import React from 'react';
import PropTypes from 'prop-types';
import {
    Grid, Box, Typography, Link, useMediaQuery, useTheme
} from '@mui/material';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { List as IList, Map as IMap } from 'immutable';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { HOME_STEP } from '../routing/flowConstants';
import {
    EcoFriendlyIcon, HandDeliverIcon,
    SendIcon, TimerIcon, ToggleDigitalIcon, TogglePhysicalIcon, TogglePrintIcon
} from '../primitive/icons/pdpIcons';
import homePageV2Messages from './assets/messages/homePageMessages';
import { CARD_TYPES } from '../constants/constants';
import {
    saveSelectedCardOnBuyButtonClick,
    selectedCardType, setClickedOnUYO
} from '../landingPage/landingPageModule';

import { useFlow } from '../routing/FlowProvider';
import SkeletonLoader from '../loader/SkeletonLoader';
import { getLoaderStatus } from '../loader/loaderSelector';
import { getRemainingPlasticCardsAllowed } from '../productCartHandler/productCartSelectors';
import { setCurrency as setCurrencyActionCreator } from '../intl/intlModule';
import { trackButtonClick } from '../segment/segmentModule';
import { getDigitalFaceplateGroups, getPhysicalFaceplateGroups } from '../landingPage/landingPageSelectors';
import { digitalHighlightBulletLists } from './DigitalHighlightBulletLists';
import { physicalHighlightBulletLists } from './PhysicalHighlightBulletLists';
import { printAtHomeHighlightBulletLists } from './PrintAtHomeHighlightBulletLists';
import giftCardDetailsPageMessagesV2 from '../giftCardDetailsPage_v2/messages/giftCardDetailsPageMessages';

const FeatureItem = ({ icon, title, description }) => (
    <Box className='feature-item-container'>
        <Box className='feature-item-icon'>{icon}</Box>
        <Box>
            <Typography
                className='feature-item-title'
                variant="h3"
            >
                {title}
            </Typography>
            <Typography
                className='feature-item-description'
                variant="body2"
            >
                {description}
            </Typography>
        </Box>
    </Box>
);

FeatureItem.propTypes = {
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired
};

const LandingPageFeatureStripe = ({
    intl,
    supportsPhysical,
    supportsDigital,
    activeTab,
    saveSelectedCardType,
    digitalPrimaryFaceplate,
    physicalPrimaryFacePlate,
    setClickedOnUYOAction,
    saveSelectedCardOnBuyButtonClickAction,
    digitalCategories,
    physicalCategories,
    stepName,
    isLoading,
    trackAddAnotherButton,
    supportedElectronicCurrencyList,
    supportedPlasticCurrencyList,
    cartCurrencyCode,
    remainingPlasticCardsAllowed,
    defaultElectronicCurrency,
    defaultPlasticCurrency,
    setCurrency,
    digitalFaceplateGroups,
    physicalFaceplateGroups
}) => {
    
    
    

    const featuresMap = {
        [CARD_TYPES.Digital]: {
            title: homePageV2Messages.whyChooseEGiftsTitle,
            features: [
                {
                    icon: <TimerIcon />,
                    title: intl.formatMessage(homePageV2Messages.digitalFeature1Title),
                    description: intl.formatMessage(homePageV2Messages.digitalFeature1Description)
                },
                {
                    icon: <SendIcon />,
                    title: intl.formatMessage(homePageV2Messages.digitalFeature2Title),
                    description: intl.formatMessage(homePageV2Messages.digitalFeature2Description)
                },
                {
                    icon: <EcoFriendlyIcon />,
                    title: intl.formatMessage(homePageV2Messages.digitalFeature3Title),
                    description: intl.formatMessage(homePageV2Messages.digitalFeature3Description)
                }
            ]
        },
        [CARD_TYPES.Physical]: {
            title: homePageV2Messages.whyChoosePhysicalTitle,
            features: [
                {
                    icon: <ScheduleSendOutlinedIcon />,
                    title: intl.formatMessage(homePageV2Messages.physicalFeature1Title),
                    description: intl.formatMessage(homePageV2Messages.physicalFeature1Description)
                },
                {
                    icon: <LocalShippingOutlinedIcon />,
                    title: intl.formatMessage(homePageV2Messages.physicalFeature2Title),
                    description: intl.formatMessage(homePageV2Messages.physicalFeature2Description)
                },
                {
                    icon: <ImageOutlinedIcon />,
                    title: intl.formatMessage(homePageV2Messages.physicalFeature3Title),
                    description: intl.formatMessage(homePageV2Messages.physicalFeature3Description)
                }
            ]
        },
        [CARD_TYPES.PrintAtHome]: {
            title: homePageV2Messages.whyChoosePrintAtHomeTitle,
            features: [
                {
                    icon: <EcoFriendlyIcon />,
                    title: intl.formatMessage(homePageV2Messages.printAtHomeFeature1Title),
                    description: intl.formatMessage(homePageV2Messages.printAtHomeFeature1Description)
                },
                {
                    icon: <HandDeliverIcon />,
                    title: intl.formatMessage(homePageV2Messages.printAtHomeFeature2Title),
                    description: intl.formatMessage(homePageV2Messages.printAtHomeFeature2Description)
                }
            ]
        }
    };

    
    const currentContent = featuresMap[activeTab] || featuresMap[CARD_TYPES.Digital];
    const { features } = currentContent;
    const sectionTitle = intl.formatMessage(currentContent.title);

    
    const hasAnyDescription = features && features.some(feature => (
        feature.description.trim()
        && feature.title.trim()
    ));

    const { goToNextStep } = useFlow();

    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

    
    const activeFaceplateGroups = (activeTab === CARD_TYPES.Physical)
        ? physicalFaceplateGroups : digitalFaceplateGroups;
    const categoryCount = activeFaceplateGroups.size;
    const firstGroupFaceplates = activeFaceplateGroups.getIn([0, 'faceplates'], IList());
    const isSingleFaceplate = categoryCount <= 1 && firstGroupFaceplates.size <= 1;

    
    let sortedCardDeliveryTypeOptionsList = digitalHighlightBulletLists(intl);
    let whyChooseTitle = intl.formatMessage(giftCardDetailsPageMessagesV2.whyChooseDigitalLabel);
    if (activeTab === CARD_TYPES.PrintAtHome) {
        sortedCardDeliveryTypeOptionsList = printAtHomeHighlightBulletLists(intl);
        whyChooseTitle = intl.formatMessage(giftCardDetailsPageMessagesV2.whyChoosePrintAtHomeLabel);
    } else if (activeTab === CARD_TYPES.Physical) {
        sortedCardDeliveryTypeOptionsList = physicalHighlightBulletLists(intl);
        whyChooseTitle = intl.formatMessage(giftCardDetailsPageMessagesV2.whyChoosePlasticLabel);
    }

    
    
    const cartCurrencyExistsInDigital = !cartCurrencyCode
        || supportedElectronicCurrencyList.includes(cartCurrencyCode);
    const cartCurrencyExistsInPhysical = !cartCurrencyCode
        || supportedPlasticCurrencyList.includes(cartCurrencyCode);

    const handleSubmit = (giftCardDeliveryType, categories, submitButtonTitleName) => {
        const isPhysicalCard = giftCardDeliveryType === CARD_TYPES.Physical;

        const defaultFaceplate = isPhysicalCard ? physicalPrimaryFacePlate : digitalPrimaryFaceplate;
        const defaultCategory = categories.get(0);
        
        
        
        saveSelectedCardOnBuyButtonClickAction(defaultCategory,
            defaultFaceplate,
            submitButtonTitleName,
            isPhysicalCard);
        setClickedOnUYOAction(false);
        goToNextStep(stepName, {
            overwritingHistoryParams: {
                state: { isPhysicalCard }
            }
        });
    };

    return (
        <Box className='feature-stripe-parent-container'>
            {}
            {isMobileView && isSingleFaceplate && sortedCardDeliveryTypeOptionsList.length > 0 && (
                <Box className='mobile-single-faceplate-highlights'>
                    <Typography component='h2' className='mobile-highlights-title'>
                        {whyChooseTitle}
                    </Typography>
                    <ul className='eco-friendly-delivery-list'>
                        {sortedCardDeliveryTypeOptionsList.map(item => (
                            <li className='description-container' key={item.text}>
                                <span className='eco-Friendly-icon'>
                                    {item.icon}
                                </span>
                                <Typography component="span" className='eco-Friendly-Label'>
                                    {item.text}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            {}
            {features && hasAnyDescription && (
                <>
                    <SkeletonLoader
                        isLoading={isLoading}
                        skeletonProps={{
                            animation: 'wave',
                            variant: 'rectangular',
                            className: 'skeletonclass',
                            width: '100%',
                            height: 50,
                            style: { marginBottom: '16px' }
                        }}
                    >
                        <Typography component='h2' className='feature-stripe-title'>
                            {sectionTitle}
                        </Typography>
                    </SkeletonLoader>

                    {}
                    <Grid container spacing={2} className='feature-stripe-grid'>
                        {features && features.map(feature => (
                            feature.title.trim() && feature.description.trim() && (
                                <Grid item xs={12} md={4} key={feature.title} className='feature-stripe-grid-item'>
                                    <SkeletonLoader
                                        isLoading={isLoading}
                                        skeletonProps={{
                                            animation: 'wave',
                                            variant: 'rectangular',
                                            className: 'skeletonclass',
                                            width: '100%',
                                            height: 50
                                        }}
                                    >
                                        <FeatureItem
                                            icon={feature.icon}
                                            title={feature.title}
                                            description={feature.description}
                                        />
                                    </SkeletonLoader>
                                </Grid>
                            )))
                        }
                    </Grid>
                </>
            )}

            {}
            <Box className='other-options-container'>
                <SkeletonLoader
                    isLoading={isLoading}
                    skeletonProps={{
                        animation: 'wave',
                        variant: 'rectangular',
                        className: 'skeletonclass',
                        width: '100%',
                        height: 50
                    }}
                >
                    <Typography className='other-options-title'>
                        {intl.formatMessage(homePageV2Messages.otherOptionsTitle)}
                    </Typography>
                </SkeletonLoader>

                <Box className='other-options-buttons'>
                    <SkeletonLoader
                        isLoading={isLoading}
                        skeletonProps={{
                            animation: 'wave',
                            variant: 'rectangular',
                            className: 'skeletonclass',
                            width: '100%',
                            height: 50
                        }}
                    >
                        {supportsDigital && activeTab !== CARD_TYPES.Digital
                            && cartCurrencyExistsInDigital && (
                        
                            <Link
                                href="#"
                                className='other-options-link'
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!cartCurrencyCode) setCurrency(defaultElectronicCurrency);
                                    saveSelectedCardType(CARD_TYPES.Digital);
                                    handleSubmit(CARD_TYPES.Digital, digitalCategories,
                                        homePageV2Messages.DigitalHomeButton);
                                    trackAddAnotherButton(intl.formatMessage(homePageV2Messages.DigitalHomeButton),
                                        HOME_STEP, 'Buttons above footer');
                                }}
                            >
                                <span className='other-options-link-icon'><ToggleDigitalIcon /></span>
                                {intl.formatMessage(homePageV2Messages.DigitalHomeButton)}
                            </Link>
                        )}
                        {supportsPhysical && activeTab !== CARD_TYPES.Physical
                            && cartCurrencyExistsInPhysical && !(remainingPlasticCardsAllowed === 0)
                            && (
                                
                                <Link
                                    href="#"
                                    className='other-options-link'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (!cartCurrencyCode) setCurrency(defaultPlasticCurrency);
                                        saveSelectedCardType(CARD_TYPES.Physical);
                                        handleSubmit(CARD_TYPES.Physical, physicalCategories,
                                            homePageV2Messages.physicalGiftCardButton);
                                        trackAddAnotherButton(intl.formatMessage(homePageV2Messages.physicalGiftCardButton),
                                            HOME_STEP, 'Buttons above footer');
                                    }}
                                >
                                    <span className='other-options-link-icon'><TogglePhysicalIcon /></span>
                                    {intl.formatMessage(homePageV2Messages.physicalGiftCardButton)}
                                </Link>
                            )}
                        {supportsDigital && activeTab !== CARD_TYPES.PrintAtHome
                            && cartCurrencyExistsInDigital && (
                            
                            <Link
                                href="#"
                                className='other-options-link'
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!cartCurrencyCode) setCurrency(defaultElectronicCurrency);
                                    saveSelectedCardType(CARD_TYPES.PrintAtHome);
                                    handleSubmit(CARD_TYPES.Digital, digitalCategories,
                                        homePageV2Messages.printAtHomeButton);
                                    trackAddAnotherButton(intl.formatMessage(homePageV2Messages.printAtHomeButton),
                                        HOME_STEP, 'Buttons above footer');
                                }}
                            >
                                <span className='other-options-link-icon'><TogglePrintIcon /></span>
                                {intl.formatMessage(homePageV2Messages.printAtHomeButton)}
                            </Link>
                        )}
                    </SkeletonLoader>
                </Box>
            </Box>
        </Box>
    );
};

LandingPageFeatureStripe.propTypes = {
    intl: intlShape.isRequired,
    supportsPhysical: PropTypes.bool,
    supportsDigital: PropTypes.bool,
    activeTab: PropTypes.string.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired,
    stepName: PropTypes.string.isRequired,
    digitalPrimaryFaceplate: PropTypes.instanceOf(IMap),
    physicalPrimaryFacePlate: PropTypes.instanceOf(IMap),
    setClickedOnUYOAction: PropTypes.func.isRequired,
    saveSelectedCardOnBuyButtonClickAction: PropTypes.func.isRequired,
    digitalCategories: PropTypes.instanceOf(IList).isRequired,
    physicalCategories: PropTypes.instanceOf(IList).isRequired,
    isLoading: PropTypes.bool.isRequired,
    trackAddAnotherButton: PropTypes.func.isRequired,
    supportedElectronicCurrencyList: PropTypes.instanceOf(IList),
    supportedPlasticCurrencyList: PropTypes.instanceOf(IList),
    remainingPlasticCardsAllowed: PropTypes.number.isRequired,
    cartCurrencyCode: PropTypes.string,
    defaultElectronicCurrency: PropTypes.string.isRequired,
    defaultPlasticCurrency: PropTypes.string.isRequired,
    setCurrency: PropTypes.func.isRequired,
    digitalFaceplateGroups: PropTypes.instanceOf(IList),
    physicalFaceplateGroups: PropTypes.instanceOf(IList)
};

LandingPageFeatureStripe.defaultProps = {
    supportsPhysical: false,
    supportsDigital: false,
    digitalPrimaryFaceplate: IMap(),
    physicalPrimaryFacePlate: IMap(),
    supportedElectronicCurrencyList: IList(),
    supportedPlasticCurrencyList: IList(),
    cartCurrencyCode: '',
    digitalFaceplateGroups: IList(),
    physicalFaceplateGroups: IList()
};

const mapDispatchToProps = {
    saveSelectedCardType: selectedCardType,
    setClickedOnUYOAction: setClickedOnUYO,
    setCurrency: setCurrencyActionCreator,
    saveSelectedCardOnBuyButtonClickAction: saveSelectedCardOnBuyButtonClick,
    trackAddAnotherButton: trackButtonClick
};

const mapStateToProps = state => ({
    isLoading: getLoaderStatus(state),
    remainingPlasticCardsAllowed: getRemainingPlasticCardsAllowed(state),
    digitalFaceplateGroups: getDigitalFaceplateGroups(state),
    physicalFaceplateGroups: getPhysicalFaceplateGroups(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(LandingPageFeatureStripe));
